<template>
  <v-card class="my-0 py-0">
    <v-card-title>Bulk Pay Report</v-card-title>
    <v-row
      no-gutters
      class="px-4 mt-n5"
    >
      <v-col cols="12">
        <v-radio-group
          v-model="policy"
          class="my-2"
          @change="resetData()"
        >
          <v-radio
            v-for="jobGroupPolicy in jobGroupPolicies"
            :key="jobGroupPolicy.id"
            :value="jobGroupPolicy.id"
            :label="jobGroupPolicy.name"
          />
        </v-radio-group>
      </v-col>
      <v-col 
        :cols="showPaymentDate ? 6 : 12"
        class="mt-n4"
      >
        <v-autocomplete
          v-model="selectedApprovalStatus"
          v-validate="'required'"
          label="Approval Status"
          item-text="name"
          item-value="id"
          :items="approvalStatusTypes"
          :disabled="!policy"
          :loading="companiesLoading"
          hide-selected
          :error-messages="errors.collect('selectedApprovalStatus')"
          data-vv-name="selectedApprovalStatus"  
          data-vv-as="status"
          @change="resetData()"
        />
      </v-col>
      <v-col 
        v-if="showPaymentDate"
        cols="6"
        class="mt-n4 pl-4"
      >
        <v-menu
          v-model="paymentDateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="paymentDateDisplay"
              v-validate="'required'"
              label="Payment Date"
              :error-messages="errors.collect('paymentDateDisplay')"
              data-vv-name="paymentDateDisplay"  
              data-vv-as="payment date"    
              readonly
              class="label-position"
              v-on="on"
            >
              <template v-slot:prepend-inner>
                <v-icon>
                  mdi-calendar
                </v-icon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="paymentDate"
            :min="today"
            no-title
            @input="paymentDateMenu = false"
          />
        </v-menu>
      </v-col>
      <v-col 
        cols="12"
        class="mt-n3"
      >
        <v-autocomplete
          v-model="selectedCompany"
          v-validate="'required'"
          label="Company"
          item-text="name"
          item-value="id"
          :items="companies"
          :disabled="!policy && !selectedApprovalStatus"
          :loading="companiesLoading"
          hide-selected
          :error-messages="errors.collect('selectedCompany')"
          data-vv-name="selectedCompany"  
          data-vv-as="company"
          @change="selectedClaimRequests = []"
        />
      </v-col>
    </v-row>
    
    <v-container
      class="full-height"
      fluid
    >
      <v-card>
        <v-row class="px-4 mt-n3">
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-data-table
              v-model="selectedClaimRequests"
              v-validate="selectedApprovalStatus !== 600 && filteredClaimRequests.length > 0 ? 'required' : ''"
              dense
              :error-messages="errors.collect('selectedClaimRequests')"
              data-vv-name="selectedClaimRequests"
              data-vv-as="employees"
              :headers="headers"
              :items="filteredClaimRequests"
              hide-default-footer
              fixed-header
              disable-pagination
              :show-select="selectedApprovalStatus !== 600"
              item-key="id"
              item-value="id"
              class="elevation-2"
              height="320px"
            >
              <template v-slot:top>
                <v-row no-gutters>
                  <v-col cols="4">
                    <h5 class="font-weight-bold py-2 mb-0 ml-5">
                      Selection:
                    </h5>
                  </v-col>
                  <v-spacer />
                  
                  <v-col cols="4">
                    <v-text-field
                      v-model="searchInput"
                      :placeholder="(selectedApprovalStatus === 200 || !selectedApprovalStatus) ? 'Filter claim by employee name' : 'Filter claim by employee name or batch number'"
                      append-icon="search"
                      label="Search"
                      hide-no-data
                      hide-selected
                    />
                  </v-col>
                </v-row>
              </template>
              <template v-slot:item.company="{ item }">
                {{ ((item.company_employee || {}).company || {}).name }}
              </template>
              <template v-slot:item.claim_cycle="{ item }">
                {{ (item.claim_cycle || {}).start_date + ' - ' + (item.claim_cycle || {}).end_date }}
              </template>
              <template v-slot:item.status="{ item }">
                <span
                  :class="{ 
                    'warning--text': (
                      item.approval_request.latest_approval.approval_status_type_id === 400
                    ),
                    'green--text': (
                      item.approval_request.latest_approval.approval_status_type_id === 200 || 
                      item.approval_request.latest_approval.approval_status_type_id === 600
                    ),
                  }"
                >
                  {{ item.approval_request.latest_approval.status.name }}
                </span>
              </template>
              <template v-slot:item.amount="{ item }">
                <div>
                  <span
                    class="font-weight-bold"
                  >
                    {{ thousandSeparator((item.total_request_amount / 100).toFixed(2)) }}
                  </span>
                </div>
              </template>
            </v-data-table>
            <span class="red--text text--lighten-1 caption">{{ errors.first('selectedClaimRequests') }}</span>
          </v-col>
           
          <v-row
            no-gutters
            class="text-center mt-n2"
          >
            <v-col cols="12">
              <v-btn
                v-if="selectedApprovalStatus === 200"
                class="ma-3 white--text"
                color="primary"
                :loading="progressing"
                :disabled="selectedClaimRequests.length <= 0"
                @click="generateBulkpayFile()"
              >
                Generate
              </v-btn>
              <v-btn
                v-if="selectedApprovalStatus === 400"
                class="ma-3 white--text"
                color="green"
                :loading="progressing"
                :disabled="revertProgressing || selectedClaimRequests.length <= 0"
                @click="updateStatus()"
              >
                Set as completed
              </v-btn>
              <v-btn
                v-if="selectedApprovalStatus === 400"
                class="ma-3 white--text"
                color="red"
                :loading="revertProgressing"
                :disabled="progressing || selectedClaimRequests.length <= 0"
                @click="revertStatus()"
              >
                Revert
              </v-btn>
            </v-col>
          </v-row>
        </v-row>
      </v-card>
    </v-container>
  </v-card>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        loading: false,
        progressing: false,
        revertProgressing: false,
        companiesLoading: false,
        showPaymentDate: false,
        paymentDateMenu: false,
        paymentDate: null,
        today: new Date().toISOString().slice(0, 10), 
        jobGroupPolicies: [],
        selectedCompany: null,
        companies: [],
        selectedApprovalStatus: null,
        approvalStatusTypes: [],
        claimRequests: [],
        policy: null,
        selectedClaimRequests: [],
        searchInput: '',
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'employee.person.name',
          },
          {
            text: 'Company',
            align: 'left',
            sortable: true,
            value: 'company',
          },
          {
            text: 'Department',
            align: 'left',
            sortable: true,
            value: 'employee.department.name',
          },
          {
            text: 'Claim Cycle',
            align: 'left',
            sortable: true,
            value: 'claim_cycle',
          },
          {
            text: 'Reference No.',
            align: 'left',
            sortable: false,
            value: 'reference_number',
          }, 
          {
            text: 'Status',
            align: 'left',
            sortable: false,
            value: 'status',
          }, 
          {
            text: 'Claim Amount (RM)',
            align: 'right',
            sortable: false,
            value: 'amount',
          },       
        ],
        claimTransactionDateValidityRules: [],
      }
    },
    computed: {
      paymentDateDisplay () {
        return this.paymentDate ? this.$moment(this.paymentDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      filteredClaimRequests () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
        let primaryFilterClaimRequests = this.claimRequests.filter(function (request) {
          return (
            (((request.employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1) ||
            (((request.claim_bulkpay_batch || {}).full_batch_number || '').indexOf(_this.searchInput) !== -1)
          ) 
        })

        let filteredCompanies = primaryFilterClaimRequests.filter(function (request) {
          return (request.company_employee || {}).company_uid === _this.selectedCompany 
        })

        let filteredStatus = filteredCompanies.filter(function (request) {
          return ((request.approval_request || {}).latest_approval || {}).approval_status_type_id === _this.selectedApprovalStatus
        })
        return filteredStatus
      },
    },
    watch: {
      selectedApprovalStatus: {
        immediate: true,
        handler (value) {
          if (value === 200) {
            this.showPaymentDate = true
          } else {
            this.showPaymentDate = false
          }
        },
      },
    },
    created () {
      this.loadClaimTransactionDateValidityRules()
      this.loadJobGroupPolicy()
      this.loadApprovalStatus()
    },
    methods: {
      loadClaimTransactionDateValidityRules () {
        this.$api.claimTransactionDateValidityRules().list()
          .then(response => {
            this.claimTransactionDateValidityRules = response.data
          })
      },
      loadJobGroupPolicy () {
        this.loading = true
        
        this.$api.jobGroupPolicies().list()
          .then(res => {
            this.jobGroupPolicies = res.data.filter(policy => {
              return policy.cutoff_type_id
            })

            this.loading = false
          })
      },
      loadApprovalStatus () {
        this.$api.approvalStatus().list()
          .then(response => {
            let statusType = response.data.filter(item => {
              return item.id === 200 || item.id === 400 || item.id === 600
            })
            this.approvalStatusTypes = _.orderBy(statusType, ['id'], ['asc'])
          })
      },
      resetData () {
        this.companies = []
        this.selectedClaimRequests = []
        this.loadClaimCycleRequests()
      },
      loadClaimCycleRequests () {
        if (this.policy && this.selectedApprovalStatus) {
          this.companiesLoading = true
          const maxEndDay = this.claimTransactionDateValidityRules[this.claimTransactionDateValidityRules.length - 1].end_day
          
          this.$api.claimBulkpays().list({ policy: this.policy, status: this.selectedApprovalStatus })
            .then(res => {
              this.claimRequests = _.orderBy(res.data.map(request => {
                const requestDate = this.$moment(request.request_date, 'YYYY-MM-DD')

                let matchedCompanyEmployees = request.employee.company_employees.filter(ce => {
                  const startDate = this.$moment(ce.start_date, 'YYYY-MM-DD')
                  const endDate = ce.end_date ? this.$moment(ce.end_date, 'YYYY-MM-DD') : null
                  const cutoffDate = this.$moment(requestDate).subtract(maxEndDay, 'days')

                  return (
                    requestDate.isSameOrAfter(startDate) &&
                    (!endDate || requestDate.isSameOrBefore(endDate) || endDate.isSameOrAfter(cutoffDate))
                  )
                })

                if (request.approval_request.latest_approval.approval_status_type_id === 200) {
                  if (request.claim_request_company) {
                    matchedCompanyEmployees = matchedCompanyEmployees.filter(ce => ce.company_uid === request.claim_request_company.company_uid)
                  } else {
                    if (matchedCompanyEmployees.some(ce => ce.is_active_pay_status === 1)) {
                      matchedCompanyEmployees = matchedCompanyEmployees.filter(ce => ce.is_active_pay_status === 1)
                    }
                  }
                } else { // pending reimbursement and completed will have claim_bulkpay_batch
                  if (request.claim_bulkpay_batch) {
                    matchedCompanyEmployees = matchedCompanyEmployees.filter(ce => ce.company_uid === request.claim_bulkpay_batch.company_uid)
                  } else if (request.claim_request_company) { // special case for company that do not generate bulkpay but claim through system
                    matchedCompanyEmployees = matchedCompanyEmployees.filter(ce => ce.company_uid === request.claim_request_company.company_uid)
                  }
                }

                const latestCompanyEmployee = _.maxBy(matchedCompanyEmployees, ce =>
                  this.$moment(ce.created_at, 'YYYY-MM-DD').valueOf() // backend is latest company employee, so cannot use start_date
                )

                return {
                  ...request,
                  company_employee: latestCompanyEmployee,
                }
              }), ['employee.person.name'], ['asc'])

              this.claimRequests.forEach(request => {
                if (request.company_employee) {
                  const newCompany = request.company_employee.company

                  if (!this.companies.some(c => c.id === newCompany.id)) {
                    this.companies.push(newCompany)
                  }
                }
              })

              this.companies = this.companies.sort((a, b) => a.name.localeCompare(b.name))

              this.companiesLoading = false
            })
        }
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      updateStatus () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 
            this.progressing = true
            
            this.$api.claimBulkpays().update(this.prepareFormData())
              .then(res => {
                this.$snack({ message: res.data.status })

                this.selectedClaimRequests = []
                this.loadClaimCycleRequests()
              })
              .catch(err => {
                if (err.response.status === 405) {
                  const formattedMessage = err.response.data.status.replace(/<br\s*\/?>/gi, '\n')
                  
                  this.loadClaimCycleRequests()

                  this.$dialog({
                    title: 'Insufficient balance',
                    message: formattedMessage, 
                    color: 'error',
                  })
                } else {
                  this.$snack({ 
                    color: 'error',
                    message: 'Failed to update status', 
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      revertStatus () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 
            this.revertProgressing = true
            
            this.$api.claimBulkpays().revert(this.prepareFormData())
              .then(res => {
                this.$snack({ message: res.data.status })

                this.selectedClaimRequests = []
                this.loadClaimCycleRequests()
              })
              .catch((error) => {
                if (error.response.status === 422) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.message, 
                  })
                  this.loadClaimCycleRequests()
                } else {
                  this.$snack({ 
                    color: 'error',
                    message: 'Failed to revert failed record status', 
                  })
                }
              })
              .then(() => {
                this.revertProgressing = false
              })
          })
      },
      generateBulkpayFile () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 
            this.progressing = true
            
            this.$api.claimBulkpays().generate(this.prepareFormData())
              .then(res => {
                const blob = new Blob([res.data], { type: 'text/csv' })

                const disposition = res.headers['content-disposition']
                let filename = 'default.csv'

                if (disposition && disposition.indexOf('filename=') !== -1) {
                  filename = disposition
                    .split('filename=')[1]
                    .split(';')[0]
                    .replace(/"/g, '')
                }

                const link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', filename)
                link.click()
                this.progressing = false

                this.selectedClaimRequests = []
                this.loadClaimCycleRequests()

                this.$snack({ message: 'Bulk pay file downloaded successfully' })
              })
              .catch(error => {
                this.progressing = false

                error.response.data.text().then(text => {
                  try {
                    const json = JSON.parse(text) // Convert to object
                    const message = json.status || 'Unknown error'

                    if (error.response.status >= 400 && error.response.status < 500) {
                      this.$snack({
                        color: 'error',
                        message: message,
                      })
                    }
                  } catch (e) {
                    // If it's not valid JSON
                    this.$snack({
                      color: 'error',
                      message: text || 'Error generating bulkpay file.',
                    })
                  }
                })
              })
          })
      },
      prepareFormData () {
        if (this.selectedApprovalStatus === 200) {
          return {
            claimRequestIds: this.selectedClaimRequests.map(req => req.id),
            company_uid: this.selectedCompany,
            payment_date: this.paymentDate,
          }
        } else {
          return {
            claimRequestIds: this.selectedClaimRequests.map(req => req.id),
            company_uid: this.selectedCompany,
          }
        }
      },
    },
  }
</script>
